/**
 * Admin → Recent Purchases tab.
 * Reads from GET /api/admin/purchases. Lets you mark an order as fulfilled
 * with a short note. Powered by the `stripe_events` collection populated by
 * the Stripe webhook.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch, adminPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, Circle, AlertTriangle, Send } from "lucide-react";

const RESEND_PRODUCTS = [
  { match: "beyond the grant",          label: "Beyond the Grant™ Replay ($10)" },
  { match: "ultimate bundle",           label: "Ultimate Nonprofit Success Bundle" },
  { match: "funding readiness bundle",  label: "Funding Readiness Bundle" },
  { match: "choose any three",          label: "Choose Any Three Toolkits" },
  { match: "funding readiness toolkit", label: "Funding Readiness Toolkit" },
  { match: "evidence bank",             label: "Evidence Bank Toolkit" },
  { match: "program blueprint",         label: "Program Blueprint Toolkit" },
  { match: "board governance",          label: "Board Governance Toolkit" },
  { match: "grant success",             label: "Grant Success Toolkit" },
  { match: "grant problem statement",   label: "Grant Problem Statement Template" },
  { match: "fundraising toolkit",       label: "Fundraising Toolkit" },
  { match: "nonprofit operations",      label: "Nonprofit Operations Toolkit" },
  { match: "group + private",           label: "Work With Donja — Group + Private 1:1" },
  { match: "group session",             label: "Work With Donja — Group Session" },
  { match: "ai build lab",              label: "AI Build Lab for Nonprofits" },
  { match: "ai agent",                  label: "Build Your Nonprofit AI Agent" },
];

function money(cents, ccy) {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: (ccy || "usd").toUpperCase(), minimumFractionDigits: 0,
  }).format((cents || 0) / 100);
}
function shortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}

export default function RecentPurchases({ pw }) {
  const [data, setData] = useState(null);   // { purchases, count, total_all_time, gross_cents }
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [noteById, setNoteById] = useState({});

  // Manual resend form
  const [resendEmail, setResendEmail] = useState("");
  const [resendFirst, setResendFirst] = useState("");
  const [resendMatch, setResendMatch] = useState("beyond the grant");
  const [resendBusy, setResendBusy] = useState(false);

  // Webhook diagnostics
  const [diag, setDiag] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, dg] = await Promise.all([
        adminFetch("/admin/purchases?limit=200", pw),
        adminFetch("/admin/stripe/webhook-diagnostics", pw).catch(() => null),
      ]);
      setData(d);
      if (dg) setDiag(dg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load purchases");
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const rows = data?.purchases || [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.email, r.customer_name, r.product_label, r.product_name, r.session_id]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [data, q]);

  const markFulfilled = async (event_id) => {
    setBusyId(event_id);
    try {
      await adminPost("/admin/purchases/mark-fulfilled", pw, {
        event_id,
        note: (noteById[event_id] || "").trim(),
      });
      toast.success("Marked as fulfilled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId("");
    }
  };

  const resendWelcome = async () => {
    const email = (resendEmail || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setResendBusy(true);
    try {
      const r = await adminPost("/admin/library/resend-welcome", pw, {
        email,
        product_match: resendMatch,
        first_name: (resendFirst || "there").trim() || "there",
        backfill_purchase: true,
      });
      if (r?.email_id) {
        toast.success(`Welcome email sent to ${email}`);
      } else {
        toast.warning(`Email attempted (no id returned) — check Resend logs`);
      }
      setResendEmail("");
      setResendFirst("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResendBusy(false);
    }
  };

  const totalRevenue = data?.gross_cents || 0;
  const totalOrders = data?.total_all_time || 0;
  const unfulfilledCount = (data?.purchases || []).filter(
    (p) => p.handled && !p.fulfilled_at,
  ).length;

  return (
    <div className="space-y-5" data-testid="admin-recent-purchases">
      {diag && diag.health !== "green" && (
        <div
          data-testid="webhook-diagnostic-card"
          className={`rounded-2xl border p-5 ${
            diag.health === "red"
              ? "border-red-400 bg-red-50/70"
              : "border-amber-400 bg-amber-50/70"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 mt-0.5 ${diag.health === "red" ? "text-red-700" : "text-amber-700"}`}>
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1">
              <div className={`text-[11px] uppercase tracking-[0.18em] font-mono-spec font-bold ${diag.health === "red" ? "text-red-800" : "text-amber-800"}`}>
                Stripe webhook · {diag.health === "red" ? "not working" : "needs attention"}
              </div>
              <div className="text-[14px] mt-1 text-ink font-medium">{diag.message}</div>
              {diag.suggested_action && (
                <div className="text-[13px] mt-2 text-ink/75 leading-[1.55]">
                  <strong>How to fix:</strong> {diag.suggested_action}
                </div>
              )}
              {diag.expected_webhook_url && (
                <div className="text-[12px] mt-2 text-ink/60 font-mono">
                  Endpoint URL to configure in Stripe:{" "}
                  <code className="bg-white px-1.5 py-0.5 rounded border border-[var(--line)]">
                    {diag.expected_webhook_url}
                  </code>
                </div>
              )}
              <div className="text-[11px] mt-3 text-ink/55 flex flex-wrap gap-x-4 gap-y-1">
                <span>Secret set: <strong>{diag.secret_configured ? "yes" : "NO"}</strong>{diag.secret_prefix && ` (${diag.secret_prefix}…)`}</span>
                <span>API key set: <strong>{diag.api_key_configured ? diag.api_key_prefix + "…" : "NO"}</strong></span>
                <span>Events 24h: <strong>{diag.events_last_24h}</strong></span>
                <span>Events 7d: <strong>{diag.events_last_7d}</strong></span>
                <span>Total: <strong>{diag.events_total_all_time}</strong></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Orders (all time)" value={totalOrders.toLocaleString()} />
        <StatCard label="Gross revenue" value={money(totalRevenue, "usd")} highlight />
        <StatCard label="Awaiting fulfillment" value={unfulfilledCount.toLocaleString()} warn={unfulfilledCount > 0} />
        <StatCard label="Webhook status" value={data ? "Connected" : "—"} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name, product…"
          data-testid="purchases-search"
          className="max-w-sm bg-white border border-[var(--line)]"
        />
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="btn-outline-ink rounded-full h-9 px-4 text-sm gap-1.5"
          data-testid="purchases-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </Button>
        <div className="ml-auto text-[11px] uppercase tracking-[0.18em] text-ink/50 font-mono">
          Showing {filtered.length} of {(data?.purchases || []).length}
        </div>
      </div>

      <div
        className="rounded-2xl border border-amber-300 bg-amber-50/60 p-4 space-y-3"
        data-testid="resend-welcome-panel"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-900 font-mono-spec font-bold">
            Manual resend
          </div>
          <div className="text-[13px] text-ink/70 mt-1">
            Use when a buyer says they never got their welcome email. Sends the same email the Stripe webhook would have sent, and unlocks the product in their Library.
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-ink/60 font-mono-spec">Buyer email</label>
            <Input
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="buyer@example.com"
              className="h-9 w-64 bg-white border border-[var(--line)]"
              data-testid="resend-email-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-ink/60 font-mono-spec">First name</label>
            <Input
              value={resendFirst}
              onChange={(e) => setResendFirst(e.target.value)}
              placeholder="there"
              className="h-9 w-40 bg-white border border-[var(--line)]"
              data-testid="resend-first-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-ink/60 font-mono-spec">Product</label>
            <select
              value={resendMatch}
              onChange={(e) => setResendMatch(e.target.value)}
              className="h-9 rounded-md bg-white border border-[var(--line)] px-2 text-[13px] min-w-[240px]"
              data-testid="resend-product-select"
            >
              {RESEND_PRODUCTS.map((p) => (
                <option key={p.match} value={p.match}>{p.label}</option>
              ))}
            </select>
          </div>
          <Button
            onClick={resendWelcome}
            disabled={resendBusy}
            className="h-9 rounded-full px-4 gap-1.5 text-[12px] font-bold uppercase tracking-wider"
            data-testid="resend-welcome-btn"
          >
            <Send size={13} /> {resendBusy ? "Sending…" : "Resend email"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--line)] hover:bg-transparent">
              <TableHead className="text-ink/60">When</TableHead>
              <TableHead className="text-ink/60">Buyer</TableHead>
              <TableHead className="text-ink/60">Product</TableHead>
              <TableHead className="text-ink/60 text-right">Amount</TableHead>
              <TableHead className="text-ink/60">Email sent</TableHead>
              <TableHead className="text-ink/60">Fulfillment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-ink/60">
                  {loading ? "Loading…" : "No purchases yet. Once someone completes a Stripe checkout you'll see them here."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => (
              <TableRow key={p.event_id} className="border-[var(--line)]" data-testid={`purchase-row-${p.event_id}`}>
                <TableCell className="whitespace-nowrap text-[13px]">{shortDate(p.created_at)}</TableCell>
                <TableCell>
                  <div className="font-medium">{p.customer_name || "—"}</div>
                  <div className="text-[12px] text-ink/60">{p.email || "—"}</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-[13px]">{p.product_label || "—"}</div>
                  {p.error && (
                    <div className="text-[11px] text-red-600 flex items-center gap-1 mt-0.5">
                      <AlertTriangle size={11} /> {p.error}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap font-medium">
                  {money(p.amount_total, p.currency)}
                </TableCell>
                <TableCell>
                  {p.welcome_email_id ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-300">
                      Sent
                    </Badge>
                  ) : p.handled ? (
                    <Badge variant="outline" className="border-amber-300 text-amber-800">
                      Mocked
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-slate-300 text-slate-500">
                      —
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {p.fulfilled_at ? (
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1 text-emerald-700 text-[12.5px] font-medium">
                        <CheckCircle2 size={13} /> {shortDate(p.fulfilled_at)}
                      </div>
                      {p.fulfilled_note && (
                        <div className="text-[11px] text-ink/55 max-w-[180px] truncate" title={p.fulfilled_note}>
                          {p.fulfilled_note}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Note (optional)"
                        value={noteById[p.event_id] || ""}
                        onChange={(e) => setNoteById((s) => ({ ...s, [p.event_id]: e.target.value }))}
                        className="h-8 text-[12px] bg-white w-40"
                        data-testid={`purchase-note-${p.event_id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => markFulfilled(p.event_id)}
                        disabled={busyId === p.event_id}
                        className="h-8 rounded-full text-[11px] uppercase tracking-wider font-bold px-3"
                        data-testid={`purchase-mark-fulfilled-${p.event_id}`}
                      >
                        <Circle size={11} className="mr-1" /> Mark done
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight, warn }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight ? "border-brand/40 bg-brand-soft" :
        warn ? "border-amber-300 bg-amber-50" :
        "border-[var(--line)] bg-white"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec">{label}</div>
      <div className={`font-display text-xl tracking-[-0.02em] mt-0.5 ${highlight ? "text-brand" : "text-ink"}`}>{value}</div>
    </div>
  );
}
